import { useMemo } from 'react';

import { useBrandAccentColorContext } from './brandContext';
import type { FlairColor } from './colors';
import { getFlairColor } from './colors';
import { useFlairColorContext } from './colors/FlairColorContextProvider';
import type { Display } from './styles/display';
import type { FocusOutlineProps } from './styles/focusOutlines';
import type { IStyle } from './styling';
import { HighContrastSelector, IsFocusVisibleClassName } from './styling';
import { useTheme } from './theme';
import type { ColorToken } from './theme/fluent9';
import { coreuiTokens, tokens } from './theme/fluent9';

export type { Display };

export interface LinkStyleProps extends FocusOutlineProps {
  readonly className?: string;
  readonly display?: Display;
  readonly unstyled?: boolean;
  readonly color?: LinkColor;
  readonly activeColor?: LinkColor;
  readonly colorOverride?: FlairColor;
  readonly isOnDarkBackground?: boolean;
  readonly alwaysUnderline?: boolean;
}

export interface ClickableStyleProps extends LinkStyleProps {
  readonly disabled?: boolean;
}

export type LinkColor = 'default' | 'bodyText' | 'blue' | 'theme' | 'white';

const defaultLinkColorMap: Record<LinkColor, ColorToken> = {
  default: tokens.colorNeutralForeground2Link,
  bodyText: tokens.colorNeutralForeground1,
  blue: coreuiTokens.colorBlueForegroundLink,
  theme: tokens.colorNeutralForeground1,
  white: tokens.colorNeutralForegroundInverted,
};
const brandLinkColorMap: Record<LinkColor, ColorToken> = {
  default: tokens.colorNeutralForeground2Link,
  bodyText: tokens.colorNeutralForeground1,
  blue: tokens.colorBrandForegroundLink,
  theme: tokens.colorNeutralForeground1,
  white: tokens.colorNeutralForegroundInverted,
};

const getLinkColor = (color: LinkColor, shouldAlignWithBrandColor: boolean) => {
  const colorMap = shouldAlignWithBrandColor ? brandLinkColorMap : defaultLinkColorMap;

  return colorMap[color];
};

export const useLinkStyles: (styleProps: ClickableStyleProps) => IStyle = ({
  display = 'inline',
  color = 'default',
  activeColor = 'blue',
  colorOverride,
  unstyled = false,
  disabled = false,
  alwaysUnderline = false,
}) => {
  const theme = useTheme();
  const { isOnDarkBackground } = useFlairColorContext();
  const { linksUseBrandColor } = useBrandAccentColorContext();

  const styles = getStyles(
    color,
    activeColor,
    unstyled,
    disabled,
    alwaysUnderline,
    theme.isInverted,
    linksUseBrandColor,
    colorOverride,
    isOnDarkBackground
  );

  return useMemo(
    () => ({
      display,
      border: 'none',
      background: 'none',
      font: 'inherit',
      margin: 0,
      padding: 0,
      textAlign: 'inherit',
      lineHeight: 'inherit',
      overflow: 'visible',
      ...styles.base,
      selectors: {
        ':hover': styles.active,
        ':hover .y-fakeLink': styles.fakeLinkActive,
        ':hover .y-fakeLink.noUnderline, :focus .y-fakeLink.noUnderline': {
          textDecoration: 'none',
        },
        [`.${IsFocusVisibleClassName} &:focus`]: styles.active,
        [`.${IsFocusVisibleClassName} &:focus .y-fakeLink`]: styles.fakeLinkActive,
        '.y-fakeLink.noLinkColor': {
          color: 'inherit',
        },
        '.y-fakeLink': styles.fakeLink,
      },
    }),
    [display, styles]
  );
};

const getStyles = (
  color: LinkColor,
  activeColor: LinkColor,
  unstyled: boolean,
  disabled: boolean,
  alwaysUnderline: boolean,
  isInverted: boolean,
  linksUseBrandColor: boolean,
  colorOverride?: FlairColor,
  isOnDarkBackground?: boolean
) => {
  const flairColor = colorOverride && getFlairColor(colorOverride, isInverted, isOnDarkBackground);
  const linkColor = getLinkColor(color, linksUseBrandColor);
  const activeLinkColor = getLinkColor(activeColor, linksUseBrandColor);

  const defaults = {
    base: {
      color: flairColor ?? linkColor,
      textDecoration: alwaysUnderline ? 'underline' : 'none',
      cursor: 'pointer',
    },
    active: {
      color: flairColor ?? activeLinkColor,
      textDecoration: 'underline',
    },
    fakeLink: {
      color: flairColor ?? linkColor,
      selectors: {
        [HighContrastSelector]: {
          color: 'LinkText',
        },
      },
    },
    fakeLinkActive: {
      color: flairColor ?? activeLinkColor,
      textDecoration: 'underline',
      cursor: 'pointer',
      selectors: {
        [HighContrastSelector]: {
          color: 'LinkText',
        },
      },
    },
  };

  if (disabled) {
    return {
      base: {
        color: tokens.colorNeutralForegroundDisabled,
        textDecoration: 'none',
        cursor: 'auto',
      },
      active: {
        color: tokens.colorNeutralForegroundDisabled,
        textDecoration: 'none',
      },
      fakeLink: {
        color: tokens.colorNeutralForegroundDisabled,
      },
      fakeLinkActive: {
        color: tokens.colorNeutralForegroundDisabled,
        textDecoration: 'none',
        cursor: 'auto',
      },
    };
  }

  if (unstyled) {
    return {
      base: {
        ...defaults.base,
        color: 'inherit',
      },
      active: {
        ...defaults.active,
        textDecoration: 'none',
        color: 'inherit',
      },
      fakeLink: defaults.fakeLink,
      fakeLinkActive: defaults.fakeLinkActive,
    };
  }

  return defaults;
};
